/**
 * Where the plans sent to this patient stand, for the clinic (spec v1.4, Plan
 * Statuses tab: what the clinic sees). Read only; the patient acts in the app.
 *
 * The live plan reads Awaiting patient, Viewed, Question open, Accepted or Not
 * right now, with the patient's reason and check back date. Versions the clinic
 * replaced are kept behind a chevron so they do not crowd the one that matters.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, Pill, inputClass } from './ui';
import { IconChevronDown } from './icons';

interface PlanRow {
  id: string;
  version: number;
  status: string;
  clinicStatus: string;
  sentAt: string | null;
  acceptedAt: string | null;
  healthCheckFlagged: boolean;
  notNow: { reason: string; comment: string | null; checkBackOn: string | null } | null;
}

const day = (value: string | null) => (value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);

/** S-27: the signed plan, the consent and the audit trail, as a PDF. */
async function downloadRecord(planId: string, version: number): Promise<void> {
  const blob = await api.downloadPdf(`/signoff/plans/${planId}/record.pdf`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `plan-record-v${version}.pdf`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const SIGNED = ['sent', 'viewed', 'question_open', 'accepted', 'not_now', 'prescribed', 'recalled', 'superseded'];
const RECALLABLE = ['sent', 'viewed', 'question_open'];

function PlanLine({ plan }: { plan: PlanRow }) {
  const qc = useQueryClient();
  const [recalling, setRecalling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recall = useMutation({
    mutationFn: () => api.post(`/signoff/plans/${plan.id}/recall`, { reason }),
    onSuccess: () => {
      setRecalling(false);
      void qc.invalidateQueries({ queryKey: ['patient-plans'] });
    },
    onError: (caught) => setError(caught instanceof Error ? caught.message : 'Could not withdraw the plan'),
  });
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid="plan-status-row">
      <span className="text-ink">
        Version {plan.version}
        {plan.sentAt ? <span className="ml-2 text-ink-muted">sent {day(plan.sentAt)}</span> : null}
        {plan.acceptedAt ? <span className="ml-2 text-ink-muted">accepted {day(plan.acceptedAt)}</span> : null}
      </span>
      <span className="flex flex-wrap items-center gap-2">
        {plan.healthCheckFlagged && <Pill className="bg-amber-100 text-amber-900">Health check flagged</Pill>}
        <Pill className="bg-ink/5 text-ink">{plan.clinicStatus}</Pill>
        {SIGNED.includes(plan.status) && (
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            onClick={() => {
              setBusy(true);
              setError(null);
              downloadRecord(plan.id, plan.version)
                .catch((caught) => setError(caught instanceof Error ? caught.message : 'Download failed'))
                .finally(() => setBusy(false));
            }}
          >
            Plan record
          </Button>
        )}
        {RECALLABLE.includes(plan.status) && !recalling && (
          <Button size="sm" variant="secondary" onClick={() => setRecalling(true)}>
            Withdraw
          </Button>
        )}
      </span>
      {recalling && (
        <span className="flex w-full flex-wrap items-center gap-2">
          <input aria-label="Why you are withdrawing it" className={`${inputClass} flex-1`} placeholder="Why, for the record" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" disabled={reason.trim().length < 5} loading={recall.isPending} onClick={() => recall.mutate()}>
            Withdraw plan
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setRecalling(false)}>
            Keep it
          </Button>
        </span>
      )}
      {error && <span className="w-full text-xs text-red-600" role="alert">{error}</span>}
      {/* The reason belongs to a plan still set aside, or to one since replaced; not to one the patient then accepted. */}
      {plan.notNow && plan.status !== 'accepted' && plan.status !== 'prescribed' && (
        <span className="w-full text-xs text-ink-muted">
          {plan.notNow.reason}
          {plan.notNow.checkBackOn ? `, check back ${day(plan.notNow.checkBackOn)}` : ''}
          {plan.notNow.comment ? `. "${plan.notNow.comment}"` : ''}
        </span>
      )}
    </li>
  );
}

export function PlanStatus({ patientId }: { patientId: string }) {
  const plans = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => api.get<{ plans: PlanRow[] }>(`/regimens/plans/${patientId}`),
  });
  const [showOlder, setShowOlder] = useState(false);
  const rows = (plans.data?.plans ?? []).filter((p) => p.status !== 'draft');
  if (rows.length === 0) return null;

  const live = rows.filter((p) => p.status !== 'superseded');
  const older = rows.filter((p) => p.status === 'superseded');

  return (
    <div className="mb-2.5" data-testid="plan-status">
      <Card title="Plan status" subtitle="What your patient has done with the plans you sent.">
        <ul className="divide-y divide-ink/5 text-sm">
          {live.map((plan) => (
            <PlanLine key={plan.id} plan={plan} />
          ))}
        </ul>
        {older.length > 0 && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setShowOlder((was) => !was)}
              aria-expanded={showOlder}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-forest"
            >
              <IconChevronDown size={15} className={`transition-transform ${showOlder ? 'rotate-180' : ''}`} />
              {showOlder ? 'Hide earlier versions' : `Earlier versions (${older.length})`}
            </button>
            {showOlder && (
              <ul className="mt-1 divide-y divide-ink/5 text-sm">
                {older.map((plan) => (
                  <PlanLine key={plan.id} plan={plan} />
                ))}
              </ul>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
